import {
    useEffect,
    useRef,
    useState,
    useCallback,
} from "react";

import {
    MoreVertical,
    Share2,
    Facebook,
    Youtube,
   ThermometerIcon,
    MessageCircle,
} from "lucide-react";

const MainImageSlider = ({
    slides = [],
    autoPlay = true,
    interval = 15000,
}) => {
    const [current, setCurrent] = useState(0);
    const [paused, setPaused] = useState(false);
    const [menuOpenIndex, setMenuOpenIndex] = useState(null);

    const sliderRef = useRef(null);
    const touchStartX = useRef(0);

    const hasSlides = slides.length > 0;

    /* ================= SLIDE CONTROLS ================= */
    const nextSlide = useCallback(() => {
        if (!hasSlides) return;
        setCurrent((prev) => (prev + 1) % slides.length);
    }, [slides.length, hasSlides]);

    const prevSlide = useCallback(() => {
        if (!hasSlides) return;
        setCurrent((prev) =>
            prev === 0 ? slides.length - 1 : prev - 1
        );
    }, [slides.length, hasSlides]);

    /* ================= AUTO PLAY ================= */
    useEffect(() => {
        if (!hasSlides || !autoPlay || paused) return;
        const timer = setInterval(nextSlide, interval);
        return () => clearInterval(timer);
    }, [hasSlides, autoPlay, paused, interval, nextSlide]);

    /* ================= DYNAMIC HEIGHT ================= */
    useEffect(() => {
        if (!hasSlides) return;
        const activeSlide = sliderRef.current?.children[current];
        if (activeSlide) {
            sliderRef.current.style.height =
                `${activeSlide.offsetHeight}px`;
        }
    }, [current, hasSlides]);

    /* ================= AUTO CLOSE MENU ================= */
    useEffect(() => {
        setMenuOpenIndex(null);
    }, [current]);

    useEffect(() => {
        const closeMenu = () => setMenuOpenIndex(null);
        window.addEventListener("click", closeMenu);
        return () => window.removeEventListener("click", closeMenu);
    }, []);

    /* ================= SHARE LOGIC ================= */
   const handleShare = async (slide) => {
    try {
        const backendUrl =
            "https://prophet-kevin-online-website-1.onrender.com";

        const imageUrl = `${backendUrl}${slide.image}`;
        let files = [];

        try {
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            files = [
                new File(
                    [blob],
                    slide.image.split("/").pop(),
                    { type: blob.type }
                ),
            ];
        } catch {
            files = [];
        }

        const linkText =
            "To visit Prophet Kevin Online official website and get access to Divine Messages, Teachings, visions sharing, live broadcasting and more click on the link bellow:";

        const fullLink = `${backendUrl}${slide.linkTo}`;

        const shareData = {
            title: slide.title,
            text: `${slide.title}\n\n${slide.description}\n\n${linkText}\n`,
            url: fullLink,
            ...(files.length && { files }),
        };

        if (navigator.canShare && navigator.canShare(shareData)) {
            await navigator.share(shareData);
        } else if (navigator.share) {
            await navigator.share({
                title: shareData.title,
                text: shareData.text,
                url: shareData.url,
            });
        } else {
            await navigator.clipboard.writeText(
                `${shareData.text}`
            );
            alert("Link copied to clipboard!");
        }
    } catch (err) {
        console.error("Share failed:", err);
    }
};


    /* ================= SWIPE SUPPORT ================= */
    const handleTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const handleTouchEnd = (e) => {
        if (!hasSlides) return;
        const diff =
            touchStartX.current - e.changedTouches[0].clientX;
        if (diff > 50) nextSlide();
        if (diff < -50) prevSlide();
    };

    return (
        <div className="relative w-full mt-[16px]">
            {!hasSlides ? (
                <div className="h-[500px] flex items-center justify-center text-gray-400">
                    No slides available
                </div>
            ) : (
                <div
                    className="relative w-full overflow-hidden h-[500px] shadow-lg"
                    onMouseEnter={() => setPaused(true)}
                    onMouseLeave={() => setPaused(false)}
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <div
                        ref={sliderRef}
                        className="flex transition-transform duration-700 ease-in-out"
                        style={{
                            transform: `translateX(-${current * 100}%)`,
                        }}
                    >
                        {slides.map((slide, index) => (
                            <div
                                key={index}
                                className="relative min-w-full pointer-events-none"
                            >
                                <img
                                    src={slide.image}
                                    alt={slide.title}
                                    className="w-full h-[500px] object-cover"
                                />

                                {/* OVERLAY */}
                                <div className="absolute bottom-0 text-center left-0 w-full h-[500px] bg-black/50"> 
                
                                    <div className="absolute m-[2px] flex items-center rounded-full rounded-bl-[0px] justify-center p-2 bottom-0 left-0 max-w-80 h-80 bg-black/40 items-center">
                                        <div className="block">
                                            <h2 className="flex items-center justify-center text-xl sm:text-2xl md:text-3xl font-bold text-yellow-300 leading-tight">
                                                {slide.title}
                                            </h2>
                                            <p className="flex items-center justify-center mt-1 sm:mt-2 text-sm sm:text-base text-white font-semibold">
                                                {slide.description}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* MENU */}
                                <div
                                    className="pointer-events-auto absolute top-4 right-1 z-50"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <button
                                        onClick={() =>
                                            setMenuOpenIndex(
                                                menuOpenIndex === index ? null : index
                                            )
                                        }
                                        className="bg-bg p-2 rounded-full text-text"
                                    >
                                        <MoreVertical size={22} />
                                    </button>

                                    {menuOpenIndex === index && (
                                        <div className="absolute right-0 p-1 mt-2 w-60 bg-card text-text rounded shadow-lg overflow-hidden">
                                            <h3 className="text-center text-primary font-bold"> Share  to family and friends </h3>
                                            <button
                                                onClick={() => {
                                                    handleShare(slide);
                                                    setMenuOpenIndex(null);
                                                }}
                                                className="flex items-center gap-3 px-4 py-3 hover:border hover:rounded hover:border-border w-full text-left"
                                            >
                                                <Share2 size={18} /> Share
                                            </button>
                                            <h3 className="text-center text-primary font-bold">Follow Us On</h3>
                                            <a
                                                href="https://www.facebook.com/agmonline?mibextid=ZbWKwL"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-3 px-4 py-3 hover:border hover:rounded hover:border-border"
                                            >
                                                <Facebook size={18} /> Facebook
                                            </a>

                                            <a
                                                href="https://whatsapp.com/channel/0029Va8wOSWHgZWmExs8Hh02"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-3 px-4 py-3 hover:border hover:rounded hover:border-border"
                                            >
                                                <MessageCircle size={18} /> WhatsApp
                                            </a>

                                            <a
                                                href="http://www.youtube.com/@prophetkevinonline5831"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-3 px-4 py-3 hover:border hover:rounded hover:border-border"
                                            >
                                                <Youtube size={18} /> YouTube
                                            </a>

                                             <a
                                                href="https://www.tiktok.com/@prophet.kevinonline2023?_t=ZM-90dIyowKTjU&_r=1"
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="flex items-center gap-3 px-4 py-3 hover:border hover:rounded hover:border-border"
                                            >
                                                <ThermometerIcon size={18} /> TikTok
                                            </a>

                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>

                    <button
                        onClick={prevSlide}
                        className="absolute top-1/2 left-2 -translate-y-1/2 bg-bg text-text rounded-full w-10 h-10 shadow"
                    >
                        ‹
                    </button>

                    <button
                        onClick={nextSlide}
                        className="absolute top-1/2 right-2 -translate-y-1/2 bg-bg text-text rounded-full w-10 h-10 shadow"
                    >
                        ›
                    </button>
                </div>
            )}
        </div>
    );
};

export default MainImageSlider;
